/*******************************************************************************
 * Copyright (c) 2025 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {
  catchError,
  filter,
  first,
  map,
  Observable,
  ObservableInput,
  switchMap,
  throwError,
} from 'rxjs';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import {Injectable, inject} from '@angular/core';
import {Store} from '@ngrx/store';
import {TokenSelectors} from '@shared/common-core/store/selectors';
import {PspToken} from '@shared/models/psp-token';
import {LocaleService} from '@shared/common-core/config/locale/locale.service';
import {AuthActions} from '@shared/modules/auth/store/actions';
import {X_PSP_TYPE} from './x-psp-type';
import {APP_CONFIG} from '@shared/common-core/config/app-config';
import {EnvironmentConfig} from '@shared/models/environment-config';

/**
 * The Auth Interceptor intercepts HTTP requests from the application to add a JWT auth token to the HTTP Authorization header if the user is logged in
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private store = inject(Store);
  private localeService = inject(LocaleService);
  private appConfig = inject<EnvironmentConfig>(APP_CONFIG);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isOwnApiRequest(req.url)) {
      return next.handle(req);
    }

    const useBaseToken =
      req.headers.has('X-PSP-Type') &&
      req.headers.get('X-PSP-Type') === X_PSP_TYPE.useBaseToken;

    const tokenSelector = useBaseToken
      ? TokenSelectors.selectBaseToken
      : TokenSelectors.selectToken;

    return this.store.select(tokenSelector).pipe(
      first(),
      map((token: PspToken) => {
        if (!token) return req;

        let headers = req.headers
          .set('Authorization', `Bearer ${token.accessToken}`)
          .set('Expires', 'Sat, 01 Jan 2000 00:00:00 GMT')
          .set('Accept-Language', this.localeService.getLocale());

        const cloned = req.clone({headers});

        return cloned;
      }),
      switchMap((clonedReq: HttpRequest<any>) => {
        return next.handle(clonedReq).pipe(
          catchError((err): ObservableInput<any> => {
            if (err.status === HttpStatusCode.Unauthorized) {
              this.store.dispatch(AuthActions.userLogout());
            }

            return throwError(() => err);
          })
        );
      })
    );
  }

  private isOwnApiRequest(url: string): boolean {
    return (
      (!!this.appConfig.apiBaseUrl && url.startsWith(this.appConfig.apiBaseUrl)) ||
      (!!this.appConfig.apiBaseUrlV2 && url.startsWith(this.appConfig.apiBaseUrlV2)) ||
      (url.startsWith('/') && !url.startsWith('//'))
    );
  }
}
